import { CircleNotchIcon, ExportIcon } from "@phosphor-icons/react";
import ShareOfVoiceModals from "@/components/share-of-voice/modals/ShareOfVoiceModals";
import ShareOfVoiceFilterHeader from "@/components/share-of-voice/shareOfVoice-filter-header/ShareOfVoiceFilterHeader";
import { Button } from "@/components/ui/button";
import type { onApplyFn } from "@/components/ui/date-range-picker";
import { ExportShareVoiceTableCsv } from "@/data-access/share-of-voice/queries/shareOfVoice-metrics-table-queries";
import { useCsvExport } from "@/lib/hooks/useCsvExport";
import { useShareOfVoiceParamsContextRequired } from "@/store/share-of-voice/params/shareOfVoice-params.context";
import ShareOfVoiceMetricsTable from "./metrics-table/ShareOfVoiceMetricsTable";

interface ShareOfVoiceContentProps {
  filterDays: number;
  isCustomDaysRange: boolean;
  startDate?: Date;
  endDate?: Date;
  onDateRangeChange: onApplyFn;
}

const ShareOfVoiceContent = ({
  filterDays,
  isCustomDaysRange,
  startDate,
  endDate,
  onDateRangeChange,
}: ShareOfVoiceContentProps) => {
  const {
    startDate: startDateStr,
    endDate: endDateStr,
    filtersAnd,
  } = useShareOfVoiceParamsContextRequired();
  const { triggerExport, isExporting, DownloadAnchor } = useCsvExport({
    mutation: ExportShareVoiceTableCsv,
    getResponses: (data) => data.exportShareVoiceTableCsv ?? [],
    getFilename: () => `share-of-voice-${new Date().toISOString()}-export.csv`,
  });

  const handleExport = () =>
    triggerExport({
      filters: {
        dateRange: {
          startDate: startDateStr,
          endDate: endDateStr,
        },
        AND: filtersAnd.AND,
        state: "ADDED",
      },
    });

  return (
    <>
      <ShareOfVoiceModals />
      <ShareOfVoiceFilterHeader
        filterDays={filterDays}
        isCustomDaysRange={isCustomDaysRange}
        startDate={startDate}
        endDate={endDate}
        onDateRangeChange={onDateRangeChange}
      />
      <main className="min-h-screen px-10">
        <div className="p-4">
          <div className="flex justify-end mb-3">
            <Button
              variant="ghost"
              size="sm"
              className="shrink-0 h-8"
              isDisabled={isExporting}
              onPress={handleExport}
            >
              {isExporting ? (
                <CircleNotchIcon
                  className="animate-spin text-gray-600"
                  size={14}
                />
              ) : (
                <ExportIcon className="text-gray-600" size={14} />
              )}
              <span className="ml-1.5">
                {isExporting ? "Exporting..." : "Export"}
              </span>
            </Button>
          </div>
          <ShareOfVoiceMetricsTable />
        </div>
      </main>

      {DownloadAnchor}
    </>
  );
};

export default ShareOfVoiceContent;
